import { AntdSelect, Input, DateRangePicker, FeatureFlagWrapper } from "components"
import React, { useEffect, useMemo, useRef, useState } from "react"
import Button from "./Button"
import { useAppSelector } from "hooks"
import { selectBranchList } from "store/Branches/branch.slice"
import { selectEmployeeListByField, selectEmployeeListSorted } from "store/Employee/detail.slice"
import { selectPayGroupData } from "store/PayGroups/payGroup.slice"
import { selectLocationListByField, selectLocationState } from "store/Location/locationDetails.slice"
import {
	Report as ReportInterface,
	Field,
	ENTITIES,
	reportsToMask,
	DATE_PICKER,
	ReportRadio,
	REPORT_NAMES,
} from "pages/reports/report-config"
import TrainingTrackerFilters from "pages/reports/TrainingTrackerFilters"
import { ALL_FILTER_VALUE, ShiftReference } from "pages/reports/training-tracker.utils"
import moment, { Moment } from "moment"
import { selectCustomerList } from "store/Customers/customers.slice"
import { selectSDSuppliersDropdownList } from "store/StaticData/SDSuppliers.slice"
import { AllowedTo, useAbac } from "react-abac"
import { IS_SUPER_ADMIN, Permissions } from "config/constants"
import { RangeValue } from "rc-picker/lib/interface"
import { DatePicker } from "antd"
import { FeatureFlagEnums } from "types/featureFlags.type"
import { selectSDEventTypesListByFilter } from "../store/StaticData/SDEventTypes.slice"

const KEYS_TO_PRESERVE_ON_CHANGE_IF_THEY_ARE_SELECTED = ["type", "startDate", "endDate", "payrollHours"]

interface Props {
	report: ReportInterface
	onSubmit: (name: string, data: any, additionalFields?: any, cb?: any) => void
	loading?: boolean
}
const defaultTypeOption = "pdf"

const defaultDependantOns: Record<string, string> = {
	[ENTITIES.BRANCH]: "",
	[ENTITIES.SITE]: "",
	[ENTITIES.EMPLOYEE]: "",
	[ENTITIES.PAY_GROUP]: "",
}
const Report: React.FC<Props> = ({ report, onSubmit, loading = false }) => {
	const { userHasPermissions } = useAbac()
	const [formData, setFormData] = useState<any>({ type: defaultTypeOption })
	const [selectedRadioOption, setSelectedRadioOption] = useState<number>(0)
	const [selectedFields, setSelectedFields] = useState<Field[]>()
	const dependantOns = useMemo(() => {
		if (!selectedFields) return { ...defaultDependantOns }

		return selectedFields.reduce(
			(acc, field) => {
				if (field.dependsOn) {
					acc[field.name] = field.dependsOn
				}
				return acc
			},
			{ ...defaultDependantOns },
		)
	}, [selectedFields])
	const employeeDependsOn = dependantOns[ENTITIES.EMPLOYEES] || dependantOns[ENTITIES.EMPLOYEE]
	const branches = useAppSelector(selectBranchList())
	const supplier = useAppSelector(selectSDSuppliersDropdownList)
	const employees = useAppSelector(selectEmployeeListByField(employeeDependsOn, formData?.[employeeDependsOn])) || []
	const employeesDropdown = useAppSelector(selectEmployeeListSorted())
	const allLocations = useAppSelector(state => selectLocationState(state).allLocationList)
	const locationsBySelectedField =
		useAppSelector(selectLocationListByField(dependantOns.SITE, formData?.[dependantOns.SITE])) || []
	const locations = dependantOns.SITE === ENTITIES.SUPPLIER ? allLocations : locationsBySelectedField
	const payGroupList = useAppSelector(selectPayGroupData())
	const customers = useAppSelector(selectCustomerList())
	const sdEventTypes = useAppSelector(selectSDEventTypesListByFilter())
	const [tempStartDate, setTempStartDate] = useState<Moment | null>(null)
	const [tempEndDate, setTempEndDate] = useState<Moment | null>(null)
	const shouldShowPayrollHours = report.name === REPORT_NAMES.TIME_SHEET_HOURS_WORKED_BY_EMPLOYEE_DUTIES_ONLY

	const prevReport = useRef<ReportInterface>(report)

	const types = useMemo(() => {
		return userHasPermissions(Permissions.DOWNLOAD_REPORTS)
			? [
					{ title: "PDF", type: "pdf" },
					{ title: "Excel", type: "excel" },
			  ]
			: []
	}, [])

	const getOptions = (value: string) => {
		switch (value) {
			case ENTITIES.BRANCH:
				return branches?.map(({ name, _id }) => ({
					label: name,
					value: _id,
				}))
			case ENTITIES.SUPPLIER:
				return supplier
			case ENTITIES.EMPLOYEE:
				return employees?.map(({ _id, firstName, lastName }) => ({
					value: _id,
					label: `${firstName} ${lastName}`,
				}))
			case ENTITIES.SITE:
				return locations?.map(({ _id, name }) => ({
					value: _id,
					label: name,
				}))
			case ENTITIES.PAY_GROUP:
				return payGroupList?.map(({ name, _id }) => ({
					label: name,
					value: _id,
				}))
			case ENTITIES.CUSTOMER:
				return (
					customers?.map(({ name, _id }) => ({
						label: name,
						value: _id,
					})) || []
				)
			case ENTITIES.EMPLOYEES: {
				const employeeOptions = dependantOns[ENTITIES.EMPLOYEES] ? employees : employeesDropdown
				return [
					...(reportsToMask.includes(report.name)
						? [{ label: "Select Employee", value: "no-employee" }]
						: []),
					...(employeeOptions?.map(employee => ({
						label: `${employee.firstName} ${employee.lastName}`,
						value: employee._id,
					})) || []),
				]
			}
			case ENTITIES.EVENT_TYPE:
				return sdEventTypes?.map(({ name, _id }) => ({
					label: name,
					value: _id,
				}))
			default:
				return []
		}
	}

	// const isDisabled = (value: string) => {
	// 	switch (value) {
	// 		case ENTITIES.BRANCH:
	// 			return !branches.length
	// 		case ENTITIES.EMPLOYEE:
	// 			return !employees.length
	// 		case ENTITIES.LOCATION:
	// 			return !locations.length
	// 		case ENTITIES.PAY_GROUP:
	// 			return !payGroupList.length
	// 		case ENTITIES.CUSTOMER:
	// 			return !customers?.length
	// 		default:
	// 			return false
	// 	}
	// }

	const submitDisabled = useMemo(() => {
		const trainingTrackerIgnoreKeys = [
			ENTITIES.BRANCH,
			ENTITIES.EMPLOYEE,
			ENTITIES.SITE,
			ENTITIES.POST_NAME,
			ENTITIES.SHIFT_REFERENCE,
		]
		const ignoreKeys = [
			...(report?.checkBoxes?.map(cb => cb.name) || []),
			...(report.name === REPORT_NAMES.TRAINING_TRACKER ? trainingTrackerIgnoreKeys : []),
		]
		const filledCount = Object.keys(formData)
			.filter(key => !ignoreKeys.includes(key))
			.filter(key => formData[key] && formData[key] !== ALL_FILTER_VALUE).length

		return filledCount < (selectedFields?.length || 0) + 3
	}, [formData, selectedFields, report])

	useEffect(() => {
		if (report?.maxRangeInDays) {
			setFormData({})
			setTempEndDate(null)
			setTempStartDate(null)
		}

		if (report.name === prevReport.current.name) {
			setSelectedFields(report.radioButtons[0]?.fields)
			setSelectedRadioOption(0)
		} else {
			const currentOption = prevReport.current.radioButtons[selectedRadioOption]
			const firstRadio = report.radioButtons[0]
			if (currentOption) {
				const fieldFound = report.radioButtons.find(
					item => item.title.toUpperCase() === currentOption.title.toUpperCase(),
				)
				if (fieldFound) {
					const index = report.radioButtons.findIndex(
						item => item.title.toUpperCase() === fieldFound.title.toUpperCase(),
					)
					setSelectedFields(fieldFound.fields)
					setSelectedRadioOption(index)
				} else {
					setSelectedFields(firstRadio?.fields)
					setSelectedRadioOption(0)
					if (firstRadio) handleInputChange(firstRadio, 0)
				}
			} else {
				setSelectedFields(firstRadio?.fields)
				setSelectedRadioOption(0)
			}
		}
		prevReport.current = report

		const { startDate, endDate, type } = formData
		if (userHasPermissions(IS_SUPER_ADMIN)) {
			if (reportsToMask.includes(report.name)) setFormData((prev: any) => ({ ...prev, EMPLOYEES: "no-employee" }))
		} else setFormData({ type, startDate, endDate })

		if (report) {
			setFormData((prev: any) => {
				const withCheckboxes = report.checkBoxes?.reduce((acc, cb) => {
					acc[cb.name] = cb.defaultValue ?? true
					return acc
				}, {} as Record<string, boolean>)
				const next = {
					startDate: prev.startDate,
					endDate: prev.endDate,
					type: report.type[0],
					...withCheckboxes,
				}
				if (report.name === REPORT_NAMES.TRAINING_TRACKER) {
					return {
						...next,
						[ENTITIES.BRANCH]: ALL_FILTER_VALUE,
						[ENTITIES.EMPLOYEE]: ALL_FILTER_VALUE,
						[ENTITIES.SITE]: ALL_FILTER_VALUE,
						[ENTITIES.POST_NAME]: ALL_FILTER_VALUE,
						[ENTITIES.SHIFT_REFERENCE]: ShiftReference.FIRST,
					}
				}
				return next
			})
		}
	}, [report])

	const handleInputChange = (radio: ReportRadio, index: number) => {
		setFormData((prev: any) => {
			const preserved: Record<string, any> = {}
			KEYS_TO_PRESERVE_ON_CHANGE_IF_THEY_ARE_SELECTED.forEach(key => {
				if (prev[key] !== undefined) preserved[key] = prev[key]
			})
			report.checkBoxes?.forEach(cb => {
				if (prev[cb.name] !== undefined) preserved[cb.name] = prev[cb.name]
			})
			return preserved
		})

		setSelectedRadioOption(index)
		setSelectedFields(radio.fields)
	}

	const handleTrainingTrackerFieldChange = (name: string, value: any) => {
		setFormData((prev: any) => {
			const next = { ...prev, [name]: value }
			if (name === ENTITIES.BRANCH) {
				next[ENTITIES.EMPLOYEE] = ALL_FILTER_VALUE
			}
			return next
		})
	}

	return (
		<div className="flex flex-col">
			<p className="text-lg font-bold">{report.title} </p>
			<p className="w-[60%]">{report.description}</p>
			{report.radioButtons.map((radio, index) => (
				<div key={`report_${index}`}>
					<Input
						className="w-10 cursor-pointer"
						type="radio"
						name="report"
						checked={index === selectedRadioOption}
						onChange={() => handleInputChange(radio, index)}
					/>
					<span className="cursor-pointer" onClick={() => handleInputChange(radio, index)}>
						{radio.title}
					</span>
				</div>
			))}

			{report.name === REPORT_NAMES.TRAINING_TRACKER && (
				<div className="py-6">
					<TrainingTrackerFilters
						formData={formData}
						onFieldChange={handleTrainingTrackerFieldChange}
						employees={employeesDropdown || []}
						branches={branches || []}
						sites={allLocations || []}
					/>
				</div>
			)}

			{report.checkBoxes?.map((checkBox, index) => {
				const checkboxElement = (
					<div key={`checkbox_${index}`} className="flex items-center gap-2">
						<Input
							className="w-10 cursor-pointer"
							type="checkbox"
							name={checkBox.name}
							checked={formData?.[checkBox.name] ?? checkBox.defaultValue ?? true}
							onChange={e =>
								setFormData({
									...formData,
									[checkBox.name]: e.target.checked,
								})
							}
						/>
						<span className="cursor-pointer">{checkBox.label}</span>
					</div>
				)

				if (checkBox.featureFlag) {
					return (
						<FeatureFlagWrapper key={`checkbox_${index}`} name={checkBox.featureFlag.name}>
							{checkboxElement}
						</FeatureFlagWrapper>
					)
				}

				return checkboxElement
			})}

			<div className="flex flex-col gap-8 py-6">
				<div className="flex flex-wrap gap-8">
					{selectedFields?.map(({ multiple = false, ...field }) => {
						return (
							<React.Fragment key={field.name}>
								<>
									{field.name === ENTITIES.EMPLOYEES && reportsToMask.includes(report.name) ? (
										<FeatureFlagWrapper name={FeatureFlagEnums.SPECIAL_FEATURE}>
											<AllowedTo perform={IS_SUPER_ADMIN}>
												<AntdSelect
													isControlled
													onChange={e => {
														setFormData({
															...formData,
															[field.name]: e,
														})
													}}
													mode={multiple ? "multiple" : undefined}
													selected={formData[field.name] ?? []}
													label={field.label}
													name={field.name}
													containerClass="w-84 h-10! space-y-0"
													size="middle"
													options={getOptions(field.name)}
													maxTagLength={28}
												/>
											</AllowedTo>
										</FeatureFlagWrapper>
									) : (
										<AntdSelect
											isControlled
											onChange={e => {
												setFormData({
													...formData,
													[field.name]: e,
												})
											}}
											mode={multiple ? "multiple" : undefined}
											selected={formData[field.name] ?? []}
											label={field.label}
											name={field.name}
											containerClass="w-84 h-10! space-y-0"
											size="middle"
											options={getOptions(field.name)}
											maxTagLength={28}
										/>
									)}
								</>
							</React.Fragment>
						)
					})}
				</div>

				<div className="flex flex-wrap gap-8">
					{(() => {
						switch (report.datePicker) {
							case DATE_PICKER.DEFAULT:
								return (
									<div className="flex flex-col">
										<label>
											Select Dates{" "}
											{report?.maxRangeInDays ? `(Max Range: ${report.maxRangeInDays} days)` : ""}
										</label>

										<DateRangePicker
											style={{ border: "solid slate", height: "32px", minWidth: "336px" }}
											format="DD/MM/YYYY"
											placeholder={["DD/MM/YYYY", "DD/MM/YYYY"]}
											allowClear={true}
											value={[
												formData?.startDate && moment(formData?.startDate),
												formData?.endDate && moment(formData?.endDate),
											]}
											onCalendarChange={dates => {
												setTempStartDate(dates?.[0] ?? null)
												setTempEndDate(dates?.[1] ?? null)
											}}
											disabledDate={currentDate => {
												const maxDays = report.maxRangeInDays

												if (maxDays) {
													if (tempStartDate) {
														const maxEnd = moment(tempStartDate)
															.add(maxDays, "days")
															.endOf("day")

														return (
															currentDate.isBefore(
																moment(tempStartDate).startOf("day"),
															) || currentDate.isAfter(maxEnd)
														)
													}

													if (tempEndDate) {
														const minStart = moment(tempEndDate)
															.subtract(maxDays, "days")
															.startOf("day")

														return (
															currentDate.isBefore(minStart) ||
															currentDate.isAfter(moment(tempEndDate).endOf("day"))
														)
													}
												}

												return false
											}}
											onChange={(value: RangeValue<Moment>) => {
												const maxDays = report.maxRangeInDays

												if (value?.[0] && value?.[1]) {
													const diffInDays = value[1].diff(value[0], "days")

													if (maxDays && diffInDays > maxDays) {
														setFormData({
															...formData,
															startDate: "",
															endDate: "",
														})
														setTempStartDate(null)
														setTempEndDate(null)
														return
													}

													setFormData({
														...formData,
														startDate: value[0].toISOString(),
														endDate: value[1].toISOString(),
													})
												} else {
													setFormData({
														...formData,
														startDate: "",
														endDate: "",
													})
													setTempStartDate(null)
													setTempEndDate(null)
												}
											}}
										/>
									</div>
								)

							case DATE_PICKER.MONTHLY:
								return (
									<div className="flex flex-col">
										<label>Select Month</label>
										<DatePicker
											style={{ border: "solid slate", height: "32px", minWidth: "336px" }}
											picker="month"
											size="large"
											placeholder="Select Month"
											format={"DD/MM/YYYY"}
											value={formData.startDate && moment(formData.startDate)}
											onChange={value => {
												if (value) {
													const startDate = shouldShowPayrollHours
														? moment(value).date(6).startOf("day")
														: moment(value).startOf("month")
													const endDate = shouldShowPayrollHours
														? moment(value).add(1, "month").date(5).endOf("day")
														: moment(value).endOf("month")

													setFormData({
														...formData,
														startDate,
														endDate,
													})
												}
											}}
										/>
									</div>
								)

							case DATE_PICKER.WEEKLY:
								return (
									<div className="flex flex-col">
										<label>Select Week</label>
										<DatePicker
											style={{ border: "solid slate", height: "32px", minWidth: "336px" }}
											picker="week"
											size="large"
											placeholder="Select Week"
											format={"DD/MM/YYYY"}
											value={formData.startDate && moment(formData.startDate)}
											onChange={value => {
												if (value)
													setFormData({
														...formData,
														startDate: moment(value).startOf("week").toISOString(),
														endDate: moment(value).endOf("week").toISOString(),
													})
											}}
										/>
									</div>
								)
							case DATE_PICKER.DAILY:
								return (
									<div className="flex flex-col">
										<label>Select Date</label>

										<DatePicker
											style={{ border: "solid slate", height: "32px", minWidth: "336px" }}
											size="large"
											placeholder="DD/MM/YYYY"
											format="DD/MM/YYYY"
											allowClear={true}
											value={formData?.startDate && moment(formData?.startDate)}
											onChange={value => {
												if (value) {
													setFormData({
														...formData,
														startDate: value.toISOString(),
														endDate: value.toISOString(),
													})
												}
											}}
										/>
									</div>
								)

							default:
								return <p>Please select a valid date picker option.</p>
						}
					})()}
				</div>

				{shouldShowPayrollHours && (
					<div className="flex flex-col">
						<Input
							label="Payroll Hours"
							type="number"
							min={0}
							placeholder="Payroll Hours"
							value={formData.payrollHours ?? ""}
							onChange={({ target: { value } }) => {
								setFormData({
									...formData,
									payrollHours: value === "" ? undefined : Number(value),
								})
							}}
						/>
					</div>
				)}

				<div>
					{types
						.filter(item => report.type.includes(item.type))
						.map((item, index) => (
							<React.Fragment key={index}>
								<Input
									type="radio"
									name="type"
									value={item.type}
									className="mt-10 w-10"
									checked={formData.type === item.type}
									onChange={({ target: { value } }) => {
										setFormData({ ...formData, type: value })
									}}
								/>
								<label>{item.title}</label>
							</React.Fragment>
						))}
				</div>
			</div>

			<div className="py-8">
				<Button
					disabled={submitDisabled || loading}
					loading={loading}
					onClick={() => {
						const additionalFields = report.radioButtons[selectedRadioOption]?.additionalFields
						onSubmit(report.name, formData, additionalFields)
					}}
				>
					Create Report
				</Button>
			</div>
		</div>
	)
}

export default Report
